import api from '../api/api';

/**
 * Fetches class report data from the backend API
 * @param {string} classId - The ID of the class/halaqa to fetch reports for
 * @param {string} period - The time period for the report (all, 7days, 30days, month)
 * @param {string} type - The type of assignment to report on (memorization, revision)
 * @returns {Promise} - Promise resolving to the report data
 */
export const fetchClassReport = async (classId, period = 'all', type = 'memorization') => {
  const response = await api.get(`/reports/class/${classId}`, {
    params: {
      period,
      type
    }
  });
  
  return response.data.success
    ? response.data.data
    : response.data;
};

/**
 * Formats a rating as a string with fixed decimal
 * @param {number} rating - The rating value
 * @returns {string} - Formatted rating string
 */
export const formatRating = (rating) => {
  if (rating === undefined || rating === null) return '0.0';
  return rating.toFixed(1);
};

/**
 * Formats a percentage value
 * @param {number} value - The percentage value
 * @returns {string} - Formatted percentage string
 */
export const formatPercent = (value) => {
  if (value === undefined || value === null) return '0%';
  return `${Math.round(value)}%`;
};

/**
 * Calculates the trend data for a value compared to a reference
 * @param {number} value - The delta/change value
 * @returns {object} - Object containing direction, color and text representation
 */
export const getTrend = (value) => {
  if (!value) return { direction: 'neutral', color: 'gray', text: '− 0%' };
  
  if (value > 0) {
    return { 
      direction: 'up', 
      color: 'green', 
      text: `↑ ${Math.abs(Math.round(value))}%` 
    };
  }
  
  return { 
    direction: 'down', 
    color: 'red', 
    text: `↓ ${Math.abs(Math.round(value))}%` 
  };
};

/**
 * Generate and download PDF achievement report for a student
 * @param {string} studentId - Student ID
 * @param {Object} options - Report options
 * @param {string} options.dateRange - Date range ('last7days', 'last30days', 'custom')
 * @param {string} options.reportType - Report type ('achievement' or 'progress')
 * @param {string} options.startDate - Start date for custom range
 * @param {string} options.endDate - End date for custom range
 * @returns {Promise} - Download promise
 */
export const generateStudentAchievementPDF = async (studentId, options = {}) => {
  try {
    const response = await api.post(
      `/reports/student/${studentId}/achievement-pdf`,
      {
        dateRange: options.dateRange || 'last30days',
        reportType: options.reportType || 'achievement',
        startDate: options.startDate,
        endDate: options.endDate
      },
      {
        responseType: 'blob', // Important for PDF download
        timeout: 30000 // 30 second timeout for PDF generation
      }
    );

    // Create blob URL and trigger download
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    
    // Extract filename from response headers or create default
    const contentDisposition = response.headers['content-disposition'];
    let filename = options.reportType === 'progress' 
      ? 'Student_Progress_Report.pdf' 
      : 'Student_Achievement_Report.pdf';
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    // Create temporary link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return { success: true, filename };
    
  } catch (error) {
    console.error('PDF generation failed:', error);
    
    if (error.response?.status === 404) {
      throw new Error('Student not found');
    } else if (error.response?.status === 403) {
      throw new Error('Not authorized to generate reports for this student');
    } else if (error.code === 'ECONNABORTED') {
      throw new Error('PDF generation timed out. Please try again.');
    } else {
      throw new Error('Failed to generate PDF report. Please try again.');
    }
  }
};

export default {
  fetchClassReport,
  formatRating,
  formatPercent,
  getTrend,
  generateStudentAchievementPDF
};